import React from "react";
import { Box, Link, SxProps, Theme, Typography } from "@mui/material";
import { Layers } from "@mui/icons-material";
import { formatCurrencyBDT } from "../../utils/currency";
import {
    comboSaving,
    getComboTiers,
    getEffectivePrice,
    nextComboTier,
    samePrice,
    unitPriceFor,
} from "../../utils/productPricing";

type PricedProduct = Parameters<typeof unitPriceFor>[0];

// Combo prices are whole taka — no ".00" on them.
const taka = (n: number) => formatCurrencyBDT(n, { minimumFractionDigits: 0 });

interface ComboPriceHintProps {
    product: PricedProduct | null | undefined;
    quantity: number;
    /** The line's unit price as it stands. */
    unitPrice: number;
    /** Puts the combo price on the line — offered when the line has
     * another price. Leave out where prices can't be changed. */
    onUse?: (unitPrice: number) => void;
    sx?: SxProps<Theme>;
}

/**
 * One line under an order item about its combo prices: the saving when the
 * combo applies, a "Use" link when the quantity qualifies but the line has
 * a different price, or the next tier to aim for.
 */
export const ComboPriceHint: React.FC<ComboPriceHintProps> = ({ product, quantity, unitPrice, onUse, sx }) => {
    if (!product || !getComboTiers(product).length || !(quantity > 0)) return null;

    const combo = unitPriceFor(product, quantity);
    const qualifies = combo < getEffectivePrice(product);
    const next = nextComboTier(product, quantity);
    const caption = {
        variant: "caption" as const,
        sx: [{ display: "flex", alignItems: "center", gap: 0.5, lineHeight: 1.4 }, ...(Array.isArray(sx) ? sx : [sx])],
    };
    const icon = <Layers sx={{ fontSize: 14 }} aria-hidden />;

    if (qualifies && samePrice(Number(unitPrice), combo)) {
        return (
            <Typography {...caption} color="success.main" fontWeight={600}>
                {icon}
                Combo {taka(combo)} each · saves {taka(comboSaving(product, quantity))}
            </Typography>
        );
    }
    if (qualifies) {
        return (
            <Typography {...caption} color="text.secondary">
                {icon}
                Combo price for {quantity}: {taka(combo)} each
                {onUse && (
                    <Link component="button" type="button" variant="caption" fontWeight={600} onClick={() => onUse(combo)}>
                        Use it
                    </Link>
                )}
            </Typography>
        );
    }
    if (next) {
        return (
            <Typography {...caption} color="text.secondary">
                {icon}
                <Box component="span">
                    Combo: {next.tier.min_quantity}+ at {taka(next.tier.unit_price)} each
                </Box>
            </Typography>
        );
    }
    return null;
};

export default ComboPriceHint;
